import { useMutation, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { InferenceAPI, useTokenAndOrgId } from './utils'
import {
  CreateOrgPaymentRequest,
  CreateOrgUserPaymentRequest,
  GetOrgInvoicesRequest,
  GetOrgUserBalanceSummaryData,
  GetOrgUserInvoicesRequest,
  Invoice,
} from './inference.gen'

// Helper for formatting currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

// Helper for calculating new balance with additional amount
export const getFormattedNewBalance = (
  currentBalance: number,
  additionalAmount: number | null
) => {
  if (!additionalAmount || additionalAmount <= 0) {
    return formatCurrency(currentBalance)
  }
  return formatCurrency(currentBalance + additionalAmount)
}

// ======== useCreateOrgPayment ========

export const useCreateOrgPayment = () => {
  const { orgId } = useTokenAndOrgId()

  const getBillingUrl = (): string => {
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      return `${currentUrl.origin}${currentUrl.pathname}`
    }
    return ''
  }

  return useMutation({
    mutationFn: async (payload: Pick<CreateOrgPaymentRequest, 'amount'>) => {
      const billingUrl = getBillingUrl()

      const result = await InferenceAPI.createOrgPayment({
        request: {
          org_id: orgId,
          amount: payload.amount,
          callback_cancel_url: billingUrl,
          callback_success_url: billingUrl,
        },
      })

      return result
    },
  })
}

// ======== useCreateUserPayment ========
export const useCreateOrgUserPayment = () => {
  const getBillingUrl = (): string => {
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      return `${currentUrl.origin}${currentUrl.pathname}`
    }
    return ''
  }

  return useMutation({
    mutationFn: async (
      payload: Pick<CreateOrgUserPaymentRequest, 'amount'>
    ) => {
      const billingUrl = getBillingUrl()

      const result = await InferenceAPI.createOrgUserPayment({
        request: {
          // user_id: payload.user_id, // TODO: fix
          amount: payload.amount,
          callback_cancel_url: billingUrl,
          callback_success_url: billingUrl,
        },
      })
      return result
    },
  })
}

// ======== useGetUserBalance ========
export interface FormattedOrgUserBalanceSummary
  extends GetOrgUserBalanceSummaryData {
  netBalanceCents: number
  netBalance: number
  formattedNetBalance: string
  formattedTotal: string
  formattedSpentTotal: string
  formattedSpendingLimit: string
  formattedTotalUserBalance: string
}

export const useGetOrgUserBalanceSummary = (user_id: number) => {
  const { token, orgId } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['org-user-balance-summary', token, orgId, user_id],
    enabled: !!token && !!orgId && !!user_id,
    queryFn: async (): Promise<FormattedOrgUserBalanceSummary> => {
      const result = await InferenceAPI.getOrgUserBalanceSummary()

      const data = result.summary as GetOrgUserBalanceSummaryData
      const netBalanceCents = data.total - data.spent_total
      const netBalance = netBalanceCents / 100

      return {
        ...data,
        netBalanceCents,
        netBalance,
        formattedNetBalance: formatCurrency(netBalance),
        formattedTotal: formatCurrency(data.total / 100),
        formattedSpentTotal: formatCurrency(data.spent_total / 100),
        formattedSpendingLimit: formatCurrency(
          data.breakdown.spending_limit / 100
        ),
        formattedTotalUserBalance: formatCurrency(
          data.breakdown.user_balance / 100 // TODO: fix `total_user_balance`
        ),
      }
    },
  })
}

// ======== useGetOrgInvoices ========
export const useGetOrgInvoices = (
  filters: GetOrgInvoicesRequest,
  pageSize: number = 10
) => {
  const { token, orgId } = useTokenAndOrgId()

  return useInfiniteQuery({
    queryKey: ['org-invoices', token, orgId, filters],
    enabled: !!token && !!orgId,
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }: { pageParam: number }) => {
      const response = await InferenceAPI.getOrgInvoices({
        request: {
          start_at: filters.start_at,
          end_at: filters.end_at,
          sort_by: filters.sort_by,
          sort_direction: filters.sort_direction,
          search: filters.search,
          page_number: pageParam,
          page_size: pageSize,
        },
      })

      // Transform data here
      if (response.items) {
        response.items = response.items.map((invoice: Invoice) => {
          let invoiceTypeLabel = 'N/A'
          switch (invoice.invoice_type) {
            case 'user_credit':
              invoiceTypeLabel = 'User Credit'
              break
            case 'org_credit':
              invoiceTypeLabel = 'Organization Credit'
              break
            default:
              break
          }

          return {
            ...invoice,
            formattedAmount: formatCurrency(invoice.amount_cent / 100),
            formattedDate: new Date(invoice.created_at).toLocaleDateString(
              'en-US',
              {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              }
            ),
            invoiceTypeLabel,
          }
        })
      }

      return response
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total || 0
      const fetchedItems = allPages.length * pageSize
      return fetchedItems < totalItems ? allPages.length + 1 : undefined
    },
  })
}

// ======== useGetOrgUserInvoices ========
export const useGetOrgUserInvoices = (
  user_id: number,
  filters: Omit<GetOrgUserInvoicesRequest, 'page_number' | 'page_size'>,
  pageSize: number = 10
) => {
  const { token, orgId } = useTokenAndOrgId()

  return useInfiniteQuery({
    queryKey: ['org-user-invoices', token, orgId, user_id, filters],
    enabled: !!token && !!orgId && !!user_id,
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }: { pageParam: number }) => {
      const response = await InferenceAPI.getOrgUserInvoices({
        request: {
          // user_id, // TODO: fix
          start_at: filters.start_at,
          end_at: filters.end_at,
          sort_by: filters.sort_by,
          sort_direction: filters.sort_direction,
          page_number: pageParam,
          page_size: pageSize,
          search: filters.search,
        },
      })

      // Transform data here
      if (response.items) {
        response.items = response.items.map((invoice: Invoice) => {
          let invoiceTypeLabel = 'N/A'
          switch (invoice.invoice_type) {
            case 'user_credit':
              invoiceTypeLabel = 'User Credit'
              break
            case 'org_credit':
              invoiceTypeLabel = 'Organization Credit'
              break
            default:
              break
          }

          return {
            ...invoice,
            formattedAmount: formatCurrency(invoice.amount_cent / 100),
            formattedDate: new Date(invoice.created_at).toLocaleDateString(
              'en-US',
              {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              }
            ),
            invoiceTypeLabel,
          }
        })
      }

      return response
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total || 0
      const fetchedItems = allPages.length * pageSize
      return fetchedItems < totalItems ? allPages.length + 1 : undefined
    },
  })
}
